import { useState } from "react";
import { useCandidates, type PriorityListParams } from "@/hooks/useCandidates";
import { Priority, User } from "@/types";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { PriorityList } from "./PriorityList";
import { ChevronDown, Inbox } from "lucide-react";
import { cn } from "@/lib/utils";

interface CandidateSectionProps {
    /** The page's list filters; the backlog narrows the same way, minus the candidate flag itself. */
    params: PriorityListParams;
    onView: (p: Priority) => void;
    onEdit: (p: Priority) => void;
    onDelete: (p: Priority) => void;
    currentUser?: User | null;
    isAdmin: boolean;
}

/** Deliverables missing a DRI or a PPP — they exist but do not rank. */
export function CandidateSection({ params, onView, onEdit, onDelete, currentUser, isAdmin }: CandidateSectionProps) {
    const [open, setOpen] = useState(false);
    const query = useCandidates(params);
    const items = query.data?.data ?? [];
    const total = query.data?.total ?? 0;
    if (!query.isLoading && total === 0) return null;

    return (
        <Collapsible open={open} onOpenChange={setOpen} className="mt-8 rounded-xl border border-dashed border-amber-300/70 bg-amber-50/30 p-4 dark:border-amber-800/60 dark:bg-amber-950/10">
            <CollapsibleTrigger asChild>
                <Button variant="ghost" className="flex w-full items-center justify-between px-1 hover:bg-transparent hover:text-foreground">
                    <span className="flex items-center gap-2 text-sm font-semibold">
                        <Inbox className="h-4 w-4 text-amber-600" />
                        Candidates
                        <span className="text-xs font-normal text-muted-foreground">
                            · {total} waiting for a DRI or a PPP — not ranked
                        </span>
                    </span>
                    <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
                </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-3">
                <PriorityList
                    priorities={items}
                    isLoading={query.isLoading}
                    isError={query.isError}
                    onView={onView}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    currentUser={currentUser}
                    isAdmin={isAdmin}
                    grouped={false}
                />
                {total > items.length && (
                    <p className="mt-2 text-xs text-muted-foreground">Showing the first {items.length} of {total}.</p>
                )}
            </CollapsibleContent>
        </Collapsible>
    );
}
